export type Membership = {
  organization_id: number;
  organization__name: string;
  role: string;
};

export type User = {
  id: number;
  email: string;
  full_name: string;
  auth_provider: string;
  is_staff: boolean;
  can_publish_legislation: boolean;
  can_transfer_debug_data?: boolean;
  can_import_debug_data?: boolean;
  memberships: Membership[];
  approval_pending: boolean;
  eligible_offices: {
    id: number;
    name: string;
  }[];
  access_request: {
    id: number;
    organization_id: number;
    organization_name: string;
    status: "pending";
  } | null;
};

export type Session =
  | { authenticated: false; auth_provider: string; auth_error?: string }
  | { authenticated: true; user: User };

export type Taxpayer = {
  purchase_return_accounts: string[];
  id: number;
  vkn: string;
  title: string;
  status: "active" | "passive";
  integrator: IntegratorSummary | null;
  created_at: string;
  updated_at: string;
};

export type TaxpayerDetail = Taxpayer & {
  integrator_connections: (IntegratorSummary & {
    credentials: Record<string, string | boolean>;
  })[];
};

export type IntegratorSummary = {
  provider: string;
  provider_label: string;
  enabled: boolean;
  credentials_ready: boolean;
  configured_fields: string[];
  supported_scopes: { doc_type: string; direction: string }[];
  last_success_at: string | null;
};

export type IntegratorCredentialField = {
  name: string;
  label: string;
  required: boolean;
  secret: boolean;
  input_type: "text" | "password" | "url" | "select" | "checkbox";
  help_text: string;
  default: string | boolean;
  options: { value: string; label: string }[];
};

export type IntegratorDefinition = {
  code: string;
  label: string;
  fields: IntegratorCredentialField[];
  supported_scopes: { doc_type: string; direction: string }[];
};

export type RefundCase = {
  id: number;
  taxpayer: number;
  taxpayer_title: string;
  taxpayer_vkn: string;
  title: string;
  period_start: string;
  period_end: string;
  claim_type: string;
  transaction_code: string;
  transaction_name: string;
  document_number: string;
  document_date: string | null;
  issuing_authority: string;
  workspace_family: RefundWorkspaceFamily;
  legislation_status: {
    family: RefundWorkspaceFamily;
    warnings: { change_id: number; message: string; effective_date: string | null }[];
    blockers: { change_id: number; message: string; effective_date: string | null }[];
    can_finalize: boolean;
  };
  refund_method: string;
  status: string;
  requested_amount: string | null;
  general_vat_rate: string;
  declared_transaction_base: string;
  declared_vat_free_input_base: string;
  vat_free_input_confirmed_at: string | null;
  vat_free_input_confirmed_by: number | null;
  declared_refundable_vat: string;
  non_refundable_threshold?: string | null;
  claim_period?: string | null;
  reduced_rate_sector_code?: string;
  reduced_rate_subsector_code?: string;
  has_multiple_product_sectors: boolean;
  classification_confirmed_by: number | null;
  classification_confirmed_at: string | null;
  workflow_version: number;
  fortis_fast?: {
    sample_days: number;
    restartable?: boolean;
  } | null;
  created_at: string;
  deleted_at: string | null;
};

export type RefundWorkspaceFamily =
  | "goods_export"
  | "reduced_rate"
  | "withholding"
  | "service_exemption"
  | "investment_project"
  | "other_special";

export type TaxTransactionRule = {
  code: string;
  name: string;
  claim_type: "export" | "full_exemption" | "withholding" | "other_refund" | "reduced_rate_annual";
  calculation: string;
  workspace_family: RefundWorkspaceFamily;
  required_documents: string[];
  valid_from: string;
  valid_to: string | null;
  version: string;
  source_url: string;
  source_reference: string;
  semantic_policy: SemanticTransactionPolicy;
};

export type SemanticRequirement = {
  key: string;
  label: string;
  requirement: "required" | "conditionally_required" | "optional" | "derived";
  reason: string;
  canonical_fields: string[];
  evidence: string[];
  possible_sources: string[];
  state?: "available" | "missing" | "not_applicable";
  evidence_found?: string[];
};

export type SemanticTransactionPolicy = {
  transaction_code: string;
  name: string;
  category: string;
  family: RefundWorkspaceFamily;
  requirements: SemanticRequirement[];
  validation_rules: string[];
  reconciliation_rules: string[];
  calculation_rule: string;
  official_outputs: string[];
  architecture_status: "ready" | "legal_basis_required";
};

export type SemanticReadiness = {
  status: "READY" | "MISSING_DATA" | "NEEDS_MAPPING" | "NEEDS_CONFIRMATION" | "INCONSISTENT_DATA" | "READY_WITH_WARNINGS";
  ready: boolean;
  progress: number;
  policy: SemanticTransactionPolicy;
  requirements: SemanticRequirement[];
  missing_semantics: SemanticRequirement[];
  mapping_gaps: { source: string; object_id: number; fields: string[] }[];
  reconciliation: {
    run_id: number | null;
    blocking_conflicts: number[];
    blocking_pending: number[];
    required_rules: string[];
  };
  warnings: { code?: string; message?: string }[];
};

export type TaxRuleSnapshot = {
  effective_on: string;
  general_vat_rate: string;
  minimum_refund_claim: string;
  reduced_rate_thresholds: Record<string, string>;
  transaction_rules: TaxTransactionRule[];
  sources: string[];
};

export type Invoice = {
  id: number;
  taxpayer: number;
  doc_type: "efatura" | "earsiv" | "esmm" | "emm" | "foreign_service";
  direction: "in" | "out";
  economic_sign: 1 | -1;
  lifecycle_status: "active" | "cancelled" | "rejected";
  lifecycle_changed_at: string | null;
  original_invoice: number | null;
  uuid: string;
  invoice_no: string;
  issue_date: string | null;
  sender_vkn: string;
  sender_name: string;
  receiver_vkn: string;
  receiver_name: string;
  currency: string;
  tax_exclusive_amount: string | null;
  tax_amount: string | null;
  total_amount: string | null;
  description: string;
  quantity: string;
  download_error: string;
  has_artifact: boolean;
  has_quarantined_artifact: boolean;
  source_xml_available: boolean;
  review_code: string;
  review_explanation: string;
  analysis_warnings: string[];
};

export type InvoiceImportIssue =
  | string
  | {
      code?: string;
      severity?: "error" | "warning" | "info";
      message?: string;
      filename?: string;
      file?: string;
      row?: number;
    };

export type InvoiceImportBatch = {
  id: number;
  taxpayer: number;
  source_format: "xml" | "xlsx" | "mixed";
  status: "succeeded" | "partial" | "failed";
  direction: "auto" | "in" | "out" | "";
  doc_type: string;
  filenames: string[];
  invoice_count: number;
  line_count: number;
  error_count: number;
  warning_count: number;
  issues: InvoiceImportIssue[];
  created_at: string;
  finished_at: string | null;
};

export type RefundEntry = {
  id: number;
  kind: "input_vat" | "loaded_vat" | "sales_invoice" | "customs_declaration" | "refund_calculation" | "reduced_rate_sales";
  source_invoice: number | null;
  source_invoice_no: string;
  row_number: number;
  payload: Record<string, string>;
};

export type CalculationResult = {
  status?: string;
  summary: Record<string, string>;
  issues: { code: string; severity: "error" | "warning"; message: string }[];
};

export type AnnualIssue = {
  id?: number | string;
  code: string;
  severity: "error" | "warning" | "info";
  message: string;
  scope?: string;
  period?: string | null;
  product_id?: number | null;
  metadata?: Record<string, unknown>;
};

export type ReadinessMessage =
  | string
  | {
      code?: string;
      label?: string;
      message?: string;
      detail?: string;
      count?: number;
      metadata?: Record<string, unknown>;
    };

export type ReadinessSource = {
  code: string;
  label: string;
  status: "ready" | "warning" | "blocked" | "missing";
  received?: number;
  expected?: number;
  detail?: string;
  issues?: AnnualIssue[];
};

export type AnnualRefundReadiness = {
  ready: boolean;
  progress: number;
  missing: ReadinessMessage[];
  blockers?: ReadinessMessage[];
  warnings: ReadinessMessage[];
  sources?: ReadinessSource[];
};

export type CapacityItem = {
  code: string;
  name: string;
  annual_quantity: string;
  unit: string;
  economic_role?: "direct_material" | "internal_intermediate" | "production_overhead" | "customer_owned";
  invoice_matchable?: boolean;
};

export type CapacityAnalysis = {
  status: "analyzed" | "text_unavailable" | "unreadable" | "missing";
  confidence: "high" | "medium" | "low";
  company_title: string;
  vkn: string;
  valid_until: string;
  page_count: number;
  text_character_count: number;
  outputs: CapacityItem[];
  inputs: CapacityItem[];
  relations: Record<string, string>[];
  warnings: CapacityWarning[];
  report_count?: number;
  reports?: { id: number; filename: string; valid_until: string; confidence: "high" | "medium" | "low" }[];
};

export type CapacityWarning = {
  code: string;
  severity: "error" | "warning" | "info";
  message: string;
  details?: {
    capacity_name?: string;
    actual_quantity?: string;
    maximum_quantity?: string;
    unit?: string;
    line_count?: number;
    contributors?: {
      invoice_line_id: number;
      invoice_no: string;
      invoice_date: string;
      item_name: string;
      quantity: string;
      unit: string;
    }[];
  };
};

export type DirectLoadedVatCandidate = {
  invoice_line_id: number;
  invoice_id: number;
  invoice_no: string;
  invoice_date: string | null;
  supplier: string;
  item_name: string;
  category: string;
  category_label: string;
  load_type: "direct" | "general" | "atik" | "none";
  classification_confidence: "high" | "medium" | "low";
  classification_reason: string;
  matched_capacity_input: string;
  matched_capacity_code: string;
  match_score: number;
  quantity: string;
  unit: string;
  capacity_unit: string;
  vat_amount: string;
  account_codes: string[];
  status: "ready" | "accepted" | "review" | "classified";
  issues: string[];
  issue_codes: string[];
  reviewable: boolean;
  finding_key: string;
  review: { explanation: string; created_at: string } | null;
  includable: boolean;
  decision: "pending" | "included" | "excluded" | "not_used";
  deduction_period?: string;
  type1_period_allocations?: { period: string; source_quantity: string; eligible_vat: string; sale_line_ids: number[] }[];
  other_sale_consumption?: { sale_line_id: number; source_quantity: string }[];
  recipe_check?: {
    source_key: string; code: string; label: string; reason: string; next_step: string;
    linked_quantity: string; unlinked_quantity: string; linked_vat: string; unlinked_vat: string;
    other_sales_quantity: string; editable_recipe_keys: string[];
  };
};

export type InvoiceAnalysisCategory = {
  category: string;
  label: string;
  load_type: "direct" | "general" | "atik" | "none";
  line_count: number;
  vat_amount: string;
};

export type YearEndStockSummary = {
  year: number;
  status: "ready" | "incomplete" | "outdated";
  row_count: number;
  remaining_vat: string;
  issues: string[];
  items: { product_code: string; product_name: string; quantity: string; unit: string; remaining_vat: string }[];
};

export type CapacityWorkspace = {
  source_preferences?: { recipe?: "capacity" | "excel" | ""; opening?: "manual" | "prior_vat_lists" | "" };
  loading_recipe_checks?: Record<string, {
    unit: string;
    recipes: (CapacityWorkspace["capacity_recipes"][number] & { used_in_sales: boolean })[];
    sales: { invoice_line_id: number; invoice_id?: number; invoice_no: string; invoice_date: string; item_name: string;
      vat_rate?: string | null;
      refundable: boolean; required_quantity: string; missing_quantity: string }[];
  }>;
  failed_accounting_imports?: { id: number; filename: string; issues: { message?: string }[] }[];
  excluded_return_invoices?: { invoice_id: number; invoice__invoice_no: string; explanation: string }[];
  case_id: number;
  opening_stock_import: {
    id: number;
    filename: string;
    row_count: number;
    accepted_count: number;
    created_at: string;
    automatic?: boolean;
    prior_vat_lists?: boolean;
    source_year?: number;
  } | null;
  previous_year_stock?: YearEndStockSummary | null;
  prior_ledger?: { year: number; period_count: number; ready: boolean };
  opening_stock_source?: { ready: boolean; source: string; issues: string[]; ignored_non_stock_count?: number;
    manual_import?: { id: number; filename: string; accepted_count: number } | null;
    review_items?: { id: string; message: string }[]; excluded_items?: { id: string; message: string }[];
    lists: Partial<Record<"prior_input_vat" | "prior_loaded_vat", { id: number; filename: string; row_count: number }>> } | null;
  year_end_stock?: YearEndStockSummary | null;
  purchase_material_recipes?: CapacityWorkspace["capacity_recipes"];
  capacity_recipes: {
    recipe_key: string;
    component_key: string;
    product_code: string;
    product_name: string;
    annual_output_quantity: string;
    output_quantity: string;
    output_unit: string;
    material_code: string;
    material_name: string;
    annual_material_quantity: string;
    material_quantity: string;
    material_unit: string;
    source: string;
    source_label?: string;
    common_annual_quantity?: string;
    common_output_quantity?: string;
    edited: boolean;
    quantity_pending?: boolean;
  }[];
  capacity_recipe_state: {
    edited: boolean;
    updated_at: string;
    updated_by: number | null;
    common_needs_average?: { approved: boolean; source_label: string; total_output_quantity: string };
  };
  capacity_recipe_import: {
    filename: string;
    source_format?: string;
    recognized_product_count?: number;
    pending_quantity_recipe_count?: number;
    empty_products?: { code: string; name: string; sheet: string; row: number }[];
    source_row_count: number;
    imported_recipe_count: number;
    imported_component_count: number;
    mapped_capacity_product_count: number;
    skipped_products: string[];
    skipped_materials: { name: string; row_count: number }[];
    skipped_material_row_count: number;
    company_only_materials?: { name: string; row_count: number }[];
    company_only_material_row_count?: number;
    verified_component_count?: number;
    company_only_component_count?: number;
    canonical_imported_recipe_count?: number;
    unmatched_products?: { code: string; name: string }[];
    ambiguous_products?: { code: string; name: string }[];
    canonical_imported_component_count?: number;
    canonical_confirmed_recipe_count?: number;
    automatic_material_mapping_count?: number;
    imported_at: string;
    imported_by: number;
  } | null;
  semantic_readiness: SemanticReadiness;
  capacity_report: {
    id: number;
    filename: string;
    created_at: string;
    analysis: CapacityAnalysis;
  } | null;
  summary: {
    taxpayer_invoice_count: number;
    incoming_invoice_count: number;
    outgoing_invoice_count: number;
    declaration_count: number;
    required_declaration_count: number;
    missing_declaration_periods: string[];
    declarations_ready: boolean;
    claim_period: string;
    official_loaded_vat_count: number;
    official_loaded_vat_total: string;
    purchase_invoice_source_complete: boolean;
    purchase_invoice_source_confirmed: boolean;
    purchase_invoice_gap_periods: string[];
    purchase_invoice_integrator_gap_periods: string[];
    purchase_invoice_reconciliation_gap_periods: string[];
    purchase_invoice_syncable_doc_types: string[];
    purchase_invoice_manual_doc_types: string[];
    purchase_invoice_manual_source_required: boolean;
    purchase_invoice_sync_available: boolean;
    purchase_invoice_retry_job_id: number | null;
    incoming_efatura_count: number;
    incoming_earsiv_count: number;
    withholding_invoice_count: number;
    withholding_vat_total: string;
    verified_withholding_vat: string;
    unverified_withholding_vat: string;
    missing_kdv2_declaration_count: number;
    missing_kdv2_declaration_vat: string;
    missing_kdv2_payment_count: number;
    missing_kdv2_payment_vat: string;
    kdv2_evidence_ready: boolean;
    kdv2_unverified_invoice_ids: number[];
    multi_period_payment_count: number;
    multi_period_invoice_ids: number[];
    multi_period_payments: {
      invoice_id: number;
      invoice_no: string;
      payment_periods: string[];
      unallocated_payment_vat: string;
      action: string;
    }[];
    declared_current_deductible_vat_total: string;
    imported_purchase_invoice_vat_total: string;
    ledger_row_count: number;
    ledger_period_count: number;
    required_ledger_period_count: number;
    missing_ledger_periods: string[];
    ledger_ready: boolean;
    inventory_movement_count: number;
    current_inventory_movement_count: number;
    inventory_opening_movement_count: number;
    capacity_output_count: number;
    capacity_input_count: number;
    invoice_line_count: number;
    classified_line_count: number;
    unresolved_line_count: number;
    direct_candidate_count: number;
    direct_candidate_vat: string;
    direct_auto_ready_count: number;
    direct_auto_ready_vat: string;
    direct_accepted_count: number;
    direct_accepted_vat: string;
    direct_ready_count: number;
    direct_ready_vat: string;
    direct_review_vat: string;
    review_count: number;
    loading_pending_count: number;
    loading_pending_group_count: number;
    loading_pending_vat: string;
    loading_included_count: number;
    loading_included_vat: string;
    loading_excluded_count: number;
    loading_excluded_vat: string;
    blocking_warning_count: number;
    export_chain_ready: boolean;
    export_customs_count: number;
    export_sales_line_count: number;
    export_invoice_count: number;
    export_delivery_amount_try: string;
    kdv1_ready: boolean;
  };
  kdv1_reconciliation: {
    ready: boolean;
    period: string;
    declared?: Record<string, string>;
    calculated?: Record<string, string>;
    comparisons: {
      code: string;
      label: string;
      declared: string;
      calculated: string;
      difference: string;
      matched: boolean;
    }[];
    issues: {
      code: string;
      severity: "error";
      message: string;
      declared?: string;
      calculated?: string;
      difference?: string;
    }[];
  } | null;
  loading_engine: {
    ready: boolean;
    totals: { direct: string; general: string; atik: string; total: string };
    confirmed_none: { direct: boolean; general: boolean; atik: boolean };
    allocations: {
      id: number;
      load_type: "direct" | "general" | "atik";
      method: string;
      invoice_line_id: number;
      invoice_no: string;
      description: string;
      eligible_vat: string;
      allocation_ratio: string;
      metadata: Record<string, string | number | boolean | null>;
    }[];
    blockers: { code: string; message: string }[];
  } | null;
  loading_integrity: {
    ready: boolean;
    pending_count: number;
    pending_group_count: number;
    pending_vat: string;
    automatic_count: number;
    automatic_group_count: number;
    automatic_vat: string;
    unresolved_count: number;
    unresolved_vat: string;
    included_count: number;
    included_vat: string;
    excluded_count: number;
    excluded_vat: string;
  };
  product_identity_mapping: {
    unresolved_count: number;
    groups: {
      key: string;
      inventory_code: string;
      name: string;
      unit: string;
      gtip: string;
      direction: "in" | "out";
      movement_ids: number[];
      document_nos: string[];
      current_product_id: number | null;
      current_product_code: string;
      candidates: {
        invoice_line_id: number;
        invoice_no: string;
        invoice_code: string;
        name: string;
        unit: string;
        gtip: string;
        score: number;
        reasons: string[];
        historical_product_id: number | null;
        historical_product_code: string;
      }[];
    }[];
  };
  direct_loaded_vat: DirectLoadedVatCandidate[];
  invoice_analysis: DirectLoadedVatCandidate[];
  exceptions: DirectLoadedVatCandidate[];
  categories: InvoiceAnalysisCategory[];
  warnings: CapacityWarning[];
  production_readiness: {
    ready: boolean;
    issues: ProductionReadinessIssue[];
    blockers: ProductionReadinessIssue[];
    warnings: ProductionReadinessIssue[];
  } | null;
  manufacturer_readiness: {
    ready: boolean;
    blockers: { code: string; message: string; metadata?: Record<string, unknown> }[];
  } | null;
  can_calculate: boolean;
  latest_result: CapacityCalculation | null;
};

export type CapacityCalculation = {
  status: "draft" | "final" | "completed" | "blocked" | "limited";
  summary: Record<string, string | number | boolean | null> & {
    provisional?: boolean;
    unresolved_count?: number;
    unresolved_vat?: string;
  };
  blockers?: { code: string; message: string; metadata?: Record<string, unknown> }[];
  issues: { code: string; severity: "error" | "warning" | "info"; message: string; metadata?: Record<string, unknown> }[];
};

export type ProductionReadinessIssue = {
  code: string;
  section: string;
  severity: "error" | "warning" | "info";
  message: string;
  action: string;
};

export type CapacityCalculationResponse = {
  calculation: CapacityCalculation;
  workspace: CapacityWorkspace;
};

export type CapacityPurchaseInvoiceSyncResponse = {
  status: "syncing_purchase_invoices";
  sync_job: InvoiceSyncJob;
  workspace: CapacityWorkspace;
};

export type RefundWorkJob<T = Record<string, unknown>> = {
  id: number;
  refund_case: number;
  kind: "prepare_allocations" | "calculate";
  status: "queued" | "running" | "completed" | "failed";
  progress: number;
  result: T | null;
  error: string;
  error_context?: {
    code: string; invoice_id?: number; invoice_line_id?: number; invoice_no?: string;
    invoice_date?: string; supplier?: string; item_name?: string; instruction: string;
    question_id?: string; question_ids?: string[]; member_id?: string; member_ids?: string[];
  } | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
};

export type DataImportBatch = {
  id: number;
  taxpayer: number;
  refund_case: number | null;
  profile: number | null;
  kind: "eledger" | "general_ledger" | "inventory" | "opening_stock" | "kdv1" | "recipe";
  file_format: "xml" | "xls" | "xlsx" | "csv" | "pdf";
  status: "processing" | "succeeded" | "partial" | "failed" | "superseded";
  filename: string;
  row_count: number;
  accepted_count: number;
  error_count: number;
  warning_count: number;
  issues: ReadinessMessage[];
  metadata: Record<string, unknown>;
};

export type SubmissionCapability = {
  enabled: false;
  requested: boolean;
  provider: "disabled";
  reason: string;
  real_submission_performed: false;
  official_guide_url: string;
};

export type SubmissionPackage = {
  id: number;
  refund_case: number;
  version: number;
  schema_version: string;
  status: "blocked" | "ready" | "queued" | "submitted" | "failed";
  content_sha256: string;
  artifact_size: number;
  readiness: AnnualRefundReadiness;
  outbox_status: "disabled" | "pending" | "processing" | "sent" | "failed";
  approval: null | {
    decision: "approved" | "rejected";
    package_sha256: string;
    note: string;
    actor: number;
    created_at: string;
  };
  status_history: {
    status: string;
    message: string;
    metadata: Record<string, unknown>;
    created_at: string;
  }[];
  created_at: string;
};

export type PortalReportOutput = {
  code: string;
  title: string;
  upload_mode: "excel" | "manual_entry";
  extension: "xls" | "xlsx";
  schema_version: string;
  row_count: number;
  ready: boolean;
};

export type ReportsOverview = {
  case_id: number;
  input_vat_preparation: { ready: boolean; issues: string[] };
  remaining_stock: {
    year: number;
    status: string;
    available: boolean;
    row_count: number;
    remaining_vat: string;
    issues: string[];
    url: string | null;
  };
  scope_message?: string;
  portal: {
    ready: boolean;
    family: RefundWorkspaceFamily;
    required_lists: string[];
    outputs: PortalReportOutput[];
    blockers: ReadinessMessage[];
  };
  control_reports: {
    code: string;
    title: string;
    description: string;
    available: boolean;
    status: string;
    url: string | null;
    format: "xlsx" | "package";
  }[];
  latest_package: SubmissionPackage | null;
  submission_capability: SubmissionCapability;
};

export type PortalReportPreview = {
  code: string;
  title: string;
  upload_mode: "excel" | "manual_entry";
  extension: "xls" | "xlsx";
  schema_version: string;
  row_count: number;
  columns: { key: string; label: string; data_type: string; required: boolean }[];
  rows: Record<string, string | number | boolean | null>[];
  issues: string[];
};

export type SourceReconciliation = {
  id: number;
  status: "matched" | "pending" | "conflict";
  input_hash: string;
  period_start: string;
  period_end: string;
  summary: Record<string, number | string>;
  items: {
    id: number;
    code: string;
    status: "matched" | "pending" | "conflict" | "resolved";
    period: string | null;
    source_kind: string;
    object_type: string;
    object_id: string;
    expected_amount: string | null;
    actual_amount: string | null;
    details: Record<string, unknown>;
  }[];
  created_at: string;
};

export type CaseDocument = {
  id: number;
  refund_case: number;
  kind: string;
  filename: string;
  content_type: string;
  content_sha256: string;
  size: number;
  metadata: Record<string, unknown>;
  identity_complete: boolean;
  uploaded_by: number | null;
  created_at: string;
};

export type AllocationPreparation = {
  created_count: number;
  allocation_count: number;
  blockers: ReadinessMessage[];
  readiness: AnnualRefundReadiness;
  input_hash?: string;
  excluded_material_count?: number;
};

export type IntegratorStatus = {
  connections: number;
  invoices: number;
  jobs: Record<string, number>;
  reference_sources: number;
  reference_errors: number;
  reference_status: "compatible" | "review_required" | "incompatible";
  reference_summary: string;
  reference: GibCompatibility;
};

export type GibCompatibilityOutput = {
  code: string;
  title: string;
  status: "compatible" | "review_required" | "incompatible";
  detail: string;
  blocks_export: boolean;
  schema_version: string;
};

export type GibCompatibility = {
  status: "compatible" | "review_required" | "incompatible";
  summary: string;
  exportable_outputs: number;
  total_outputs: number;
  last_checked_at: string | null;
  monitoring_status: "current" | "delayed";
  monitoring_message: string;
  outputs: GibCompatibilityOutput[];
};

export type LegislationChange = {
  id: number;
  status: "detected" | "tax_review" | "technical_review" | "published" | "rejected";
  summary: string;
  changed_sections: string[];
  affected_transaction_codes: string[];
  affected_families: RefundWorkspaceFamily[];
  affected_rule_codes: string[];
  effective_date: string | null;
  proposed_tests: string[];
  requires_final_block: boolean;
  draft_provider: string;
  created_at: string;
  source: { code: string; title: string; authority: string; url: string; document_type: string };
  snapshot: { id: number; sha256: string; document_date: string | null; fetched_at: string; previous_sha256: string; diff: string };
  approvals: { stage: "tax" | "technical"; decision: string; reviewer: string; notes: string; created_at: string }[];
  rule_package: null | {
    version: string;
    valid_from: string;
    valid_to: string | null;
    rule_definitions: Record<string, unknown>[];
    published_rule_ids: number[];
    test_results: Record<string, unknown>;
    published_at: string;
    published_by: string;
  };
};

export type InvoiceSyncJob = {
  id: number;
  taxpayer: number;
  taxpayer_title: string;
  taxpayer_vkn: string;
  start_date: string;
  end_date: string;
  doc_types: string[];
  directions: string[];
  status: "queued" | "running" | "succeeded" | "partial" | "failed";
  attempts: number;
  error: string;
  window_summary: {
    total: number;
    completed: number;
    succeeded: number;
    issues: number;
    failed: number;
    running: number;
    retryable: number;
  };
  document_summary: { total: number; verified: number; issues: number; warnings: number };
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
};

function cookie(name: string): string {
  const value = document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith(`${name}=`));
  return value ? decodeURIComponent(value.slice(name.length + 1)) : "";
}

export class ApiError extends Error {
  status: number;
  body: Record<string, unknown>;

  constructor(status: number, message: string, body: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export type ApiFile = {
  blob: Blob;
  filename: string | null;
};

function requestHeaders(options: RequestInit): Headers {
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData) && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const csrf = cookie("csrftoken");
  if (csrf) headers.set("X-CSRFToken", csrf);
  return headers;
}

async function responseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text) as unknown;
  } catch {
    const contentType = response.headers.get("Content-Type") || "";
    if (contentType.includes("text/html") || /<\s*!doctype|<\s*html/i.test(text)) {
      return {
        detail: response.status >= 500
          ? "Sunucu hatası oluştu. Sayfayı yenileyip tekrar deneyin."
          : "İstek tamamlanamadı."
      };
    }
    return { detail: text.slice(0, 500) };
  }
}

function errorBody(value: unknown): Record<string, unknown> {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (Array.isArray(value)) return { detail: value.join(" ") };
  return { detail: value === null || value === undefined ? "" : String(value) };
}

function errorMessage(body: Record<string, unknown>): string {
  if (typeof body.detail === "string" && body.detail) return body.detail;
  const values = Object.values(body).flatMap((value) => Array.isArray(value) ? value : [value]);
  const message = values.filter((value) => value !== null && value !== undefined).join(" ");
  return message || "İşlem tamamlanamadı.";
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = requestHeaders(options);
  const response = await fetch(path, { ...options, headers, credentials: "same-origin" });
  const body = await responseBody(response);
  if (!response.ok) {
    const details = errorBody(body);
    throw new ApiError(response.status, errorMessage(details), details);
  }
  return body as T;
}

export async function apiFile(path: string, options: RequestInit = {}): Promise<ApiFile> {
  const headers = requestHeaders(options);
  const response = await fetch(path, { ...options, headers, credentials: "same-origin" });
  if (!response.ok) {
    const body = errorBody(await responseBody(response));
    throw new ApiError(response.status, errorMessage(body), body);
  }
  const disposition = response.headers.get("Content-Disposition") || "";
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  let filename = encoded || plain || null;
  if (filename) {
    try {
      filename = decodeURIComponent(filename);
    } catch {
      // Keep the server-provided fallback when it is not URI encoded.
    }
  }
  return { blob: await response.blob(), filename };
}

export function getSession(): Promise<Session> {
  return api<Session>("/api/auth/session");
}
